import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { BellOff, CheckCheck, Loader2, RotateCw } from 'lucide-react'

import PopupCloseButton from 'components/PopupCloseButton'
import { Button } from 'components/ui/button'
import { toast } from 'components/ui/toast-store'
import { cn } from 'lib/utils'
import {
	fetchMoreNotifications,
	fetchNotifications,
	markAllNotificationsRead,
	markNotificationRead,
} from 'slices/notificationSlice'

import type { NotificationItem as NotificationItemData } from '../types/notification.types'
import { NotificationItem } from './NotificationItem'

export interface NotificationListProps {
	/** Called before navigating, so the popover or dialog around it can close. */
	onNavigate?: () => void
	/** Shows a close button in the header, for the field dialog. */
	onClose?: () => void
	className?: string
}

/**
 * The bell's list (MYD-542), shared by the desktop popover and the field
 * dialog. Fetched each time it opens, so it is never staler than the badge.
 *
 * Opening an item marks it read, optimistically, and goes to its link. An item
 * with no link is just marked read.
 */
export function NotificationList({ onNavigate, onClose, className }: NotificationListProps) {
	const dispatch = useDispatch<any>()
	const navigate = useNavigate()
	const { items, nextCursor, status, loadingMore, error, unreadCount } = useSelector((state: any) => state.notifications)

	useEffect(() => {
		dispatch(fetchNotifications())
	}, [dispatch])

	const open = async (item: NotificationItemData) => {
		if (!item.readAt) dispatch(markNotificationRead(item.id))
		if (item.link) {
			onNavigate?.()
			navigate(item.link)
		}
	}

	const markAll = async () => {
		const result = await dispatch(markAllNotificationsRead())
		if (markAllNotificationsRead.rejected.match(result)) toast.error(result.payload as string)
	}

	return (
		<div className={cn('flex min-h-0 flex-col', className)}>
			<div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
				<h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
				<div className="flex items-center gap-1">
					<button
						type="button"
						onClick={markAll}
						disabled={unreadCount === 0}
						className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-[#0080fe] transition-colors hover:bg-primary/5 disabled:cursor-default disabled:text-gray-300 disabled:hover:bg-transparent"
					>
						<CheckCheck className="h-3.5 w-3.5" />
						Mark all read
					</button>
					{onClose && <PopupCloseButton onClose={onClose} />}
				</div>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto">
				{status === 'failed' ? (
					<div className="flex flex-col items-center gap-3 px-4 py-8 text-center text-sm text-gray-600" role="alert">
						{error}
						<Button variant="ghost" isOutline onClick={() => dispatch(fetchNotifications())}>
							<RotateCw className="mr-1.5 h-3.5 w-3.5" />
							Try again
						</Button>
					</div>
				) : status !== 'loaded' && items.length === 0 ? (
					<p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gray-500">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading…
					</p>
				) : items.length === 0 ? (
					// The ui-design skill's empty state, at popover scale.
					<div className="flex flex-col items-center px-4 py-10 text-center">
						<div className="mb-3 rounded-full bg-gray-100 p-3">
							<BellOff className="h-5 w-5 text-gray-400" />
						</div>
						<p className="text-sm font-medium text-gray-900">You're all caught up</p>
						<p className="mt-1 text-xs text-gray-500">New notifications will appear here.</p>
					</div>
				) : (
					<>
						<ul className="divide-y divide-gray-100" aria-label="Notifications">
							{items.map((item: NotificationItemData) => (
								<li key={item.id}>
									<NotificationItem item={item} onSelect={open} />
								</li>
							))}
						</ul>
						{nextCursor && (
							<div className="p-3 text-center">
								<Button variant="ghost" isOutline onClick={() => dispatch(fetchMoreNotifications())} disabled={loadingMore}>
									{loadingMore && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
									Load more
								</Button>
							</div>
						)}
						{error && status === 'loaded' && <p className="px-4 pb-3 text-center text-xs text-destructive">{error}</p>}
					</>
				)}
			</div>
		</div>
	)
}

export default NotificationList
